<template>
  <div class="fixed inset-0 z-60 overflow-y-auto" @click="$emit('close')">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Backdrop overlay - z-index 0 within the z-60 container -->
      <div class="fixed inset-0 transition-opacity z-0" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
      </div>

      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
        >&#8203;</span
      >

      <!-- Modal content - z-index 10 within the z-60 container, above backdrop -->
      <div
        class="relative z-10 inline-block align-bottom bg-white dark:bg-gray-800 rounded-none sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-4 sm:align-middle w-full h-[100dvh] sm:h-auto sm:w-[95vw] sm:max-w-[1200px] sm:max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="space-y-4">
            <!-- Employee Header Info -->
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
              <div>
                <!-- Name with inline billability badges -->
                <div class="flex flex-wrap items-center gap-2 sm:gap-3">
                  <div class="flex items-center gap-2">
                    <span
                      class="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      :class="statusDotColor"
                      :title="contractStatusMessage || 'Vast contract'"
                    ></span>
                    <h3 class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                      {{ employee.name }}
                    </h3>
                  </div>
                  <!-- SWP badge -->
                  <span
                    v-if="employee.simplicate_id === SWP_EMPLOYEE_ID"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                    >SWP</span
                  >
                  <!-- Billability badges inline after name (hidden for SWP) -->
                  <div
                    v-if="employee.simplicate_id !== SWP_EMPLOYEE_ID"
                    class="flex items-center gap-1.5 sm:gap-2"
                  >
                    <span
                      class="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium"
                      :class="getBillabilityBadgeClass(pastBillability)"
                    >
                      <span class="text-gray-500 dark:text-gray-400 mr-1">Decl. afgelopen 8w:</span>
                      <span :class="getBillabilityTextClass(pastBillability)">
                        {{ pastBillability !== null ? `${pastBillability}%` : '-' }}
                      </span>
                    </span>
                    <span
                      class="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium"
                      :class="getBillabilityBadgeClass(upcomingBillability)"
                    >
                      <span class="text-gray-500 dark:text-gray-400 mr-1">Decl. komende 8w:</span>
                      <span :class="getBillabilityTextClass(upcomingBillability)">
                        {{ upcomingBillability !== null ? `${upcomingBillability}%` : '-' }}
                      </span>
                    </span>
                  </div>
                </div>
                <div
                  class="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1"
                >
                  <template v-if="employee.simplicate_id === SWP_EMPLOYEE_ID">
                    <span>Externe partner — altijd beschikbaar</span>
                  </template>
                  <template v-else>
                    <span v-if="employee.function">{{ employee.function }}</span>
                    <span v-if="displayTeams !== 'Geen team'">{{ displayTeams }}</span>
                    <span v-if="employee.aanspreekpunt_name"
                      >Aanspreekpunt: {{ employee.aanspreekpunt_name }}</span
                    >
                    <span>FTE: {{ parseFloat(employee.fte_ratio) || 1.0 }}</span>
                    <span>Target: {{ displayDeclarability }}</span>
                    <span
                      v-if="showContractWarning"
                      class="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300"
                    >
                      {{ contractStatusMessage }}
                    </span>
                  </template>
                </div>
                <span class="text-xs text-gray-500 dark:text-gray-400 basis-full">
                  Periode: {{ formatPeriodDate(periodStart) }} – {{ formatPeriodDate(periodEnd) }}
                </span>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 self-start"
              >
                <XMarkIcon class="h-6 w-6" />
              </button>
            </div>

            <!-- Chart + Sidebar Layout (hidden for SWP) -->
            <div
              v-if="employee.simplicate_id !== SWP_EMPLOYEE_ID"
              class="flex flex-col sm:flex-row gap-3 sm:gap-4 border-t border-gray-200 dark:border-gray-700 pt-4"
            >
              <!-- Left: Bar Chart -->
              <div class="flex-1 min-w-0">
                <EmployeeHoursBarChart
                  v-if="employeeWithWeeks"
                  :employee="employeeWithWeeks"
                  :period-start="periodStart"
                  :period-end="periodEnd"
                  unit="hours"
                  height="280px"
                  :contract-hours-per-week="Number(employee.contractual_hours_per_week) || 40"
                  :target-billability="employee.max_billability || 80"
                  :show-billability="false"
                  :show-current-week-highlight="true"
                  @week-click="handleWeekClick"
                />
                <div
                  v-else
                  class="h-[280px] flex items-center justify-center text-gray-500 dark:text-gray-400"
                >
                  <span v-if="isLoadingWeekly">Laden...</span>
                  <span v-else>Geen beschikbaarheidsdata</span>
                </div>
              </div>

              <!-- Right: Projects & Pre-sourcings Sidebar -->
              <div class="w-full sm:w-64 md:w-80 sm:flex-shrink-0">
                <EmployeeProjectsSidebar
                  :employee-id="employee.simplicate_id"
                  :period-start="periodStart"
                  :period-end="periodEnd"
                />
              </div>
            </div>

            <!-- Notes Section -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
              <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Opmerkingen</span>
              <textarea
                v-model="employeeNotes"
                @input="updateEmployeeNotes"
                placeholder="Voeg opmerkingen toe..."
                class="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                rows="6"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            @click="$emit('close')"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { employeeService } from '@/services/employeeService.js'
import { hoursService } from '@/services/hoursService.js'
import { useScrollLock } from '@/composables/ui/useScrollLock'
import { useContractStatus } from '@/composables/domain/useContractStatus'
import EmployeeHoursBarChart from '@/components/employee/EmployeeHoursBarChart.vue'
import EmployeeProjectsSidebar from '@/components/employee/EmployeeProjectsSidebar.vue'
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'

const { getStatusDotColor, shouldShowWarning, getStatusMessage } = useContractStatus()

const props = defineProps({
  employee: {
    type: Object,
    required: true
  },
  // Optional range override, used by the employee overview date filters.
  periodStart: {
    type: String,
    default: null
  },
  periodEnd: {
    type: String,
    default: null
  }
})

defineEmits(['close'])

// Contract date computed properties
const contractEndDate = computed(() => props.employee.contract_end_date)
const contractStartDate = computed(() => props.employee.contract_start_date)
const statusDotColor = computed(() =>
  getStatusDotColor(contractEndDate.value, contractStartDate.value)
)
const showContractWarning = computed(() =>
  shouldShowWarning(contractEndDate.value, contractStartDate.value)
)
const contractStatusMessage = computed(() =>
  getStatusMessage(contractEndDate.value, contractStartDate.value)
)

// Scroll lock - lock when mounted, auto-unlocks on unmount via composable
const { lockScroll } = useScrollLock()
lockScroll()

// Use the overview's selected period when provided. Keep the original 8-week
// fallback for other callers of this reusable modal.
const periodStart = computed(() => {
  if (props.periodStart) return String(props.periodStart).split('T')[0]

  const d = new Date()
  d.setDate(d.getDate() - 56) // 8 weeks back
  return d.toISOString().split('T')[0]
})

const periodEnd = computed(() => {
  if (props.periodEnd) return String(props.periodEnd).split('T')[0]

  const d = new Date()
  d.setDate(d.getDate() + 56) // 8 weeks forward
  return d.toISOString().split('T')[0]
})

const formatPeriodDate = input => {
  const date = String(input).split('T')[0]
  const [year, month, day] = date.split('-')
  return year && month && day ? `${day}-${month}-${year}` : date
}

// Weekly availability data
const weeklyData = ref([])
const isLoadingWeekly = ref(false)

// Detailed hours for projectmanagement calculation
const detailedHours = ref([])

const fetchWeeklyData = async () => {
  if (!props.employee?.simplicate_id) {
    weeklyData.value = []
    detailedHours.value = []
    return
  }

  isLoadingWeekly.value = true
  try {
    // Fetch weekly availability and detailed hours in parallel
    const [weeklyResult, hoursResult] = await Promise.all([
      hoursService.getWeeklyAvailability({
        start_date: periodStart.value,
        end_date: periodEnd.value
      }),
      hoursService.getHours({
        employee_id: props.employee.simplicate_id,
        start_date: periodStart.value,
        end_date: periodEnd.value,
        limit: 5000
      })
    ])

    // Find this employee's data in the result
    const empData = weeklyResult.data?.find(e => e.simplicate_id === props.employee.simplicate_id)
    weeklyData.value = empData?.weeks || []
    detailedHours.value = Array.isArray(hoursResult) ? hoursResult : []
  } catch (err) {
    console.error('Error fetching weekly data:', err)
    weeklyData.value = []
    detailedHours.value = []
  } finally {
    isLoadingWeekly.value = false
  }
}

// Employee object with weeks for the chart
const employeeWithWeeks = computed(() => {
  if (!weeklyData.value.length) return null
  return {
    ...props.employee,
    weeks: weeklyData.value
  }
})

// Reload when either the employee or the selected period changes.
watch(
  () => [props.employee?.simplicate_id, periodStart.value, periodEnd.value],
  () => {
    fetchWeeklyData()
  },
  { immediate: true }
)

// Handle week click (could show details)
const handleWeekClick = () => {
  // Could emit event or show detail popup
}

const displayTeams = computed(() => {
  if (!props.employee.teams || props.employee.teams.length === 0) {
    return 'Geen team'
  }
  return props.employee.teams.map(team => team.name).join(', ')
})

const displayDeclarability = computed(() => {
  const min = props.employee.min_billability || 0
  const max = props.employee.max_billability || 0
  return `${min} - ${max}%`
})

// Helper functions for billability badge styling
const getBillabilityBadgeClass = billability => {
  if (billability === null) return 'bg-gray-100 dark:bg-gray-700'
  const target = props.employee.max_billability || 80
  if (billability >= target) return 'bg-green-100 dark:bg-green-900/30'
  if (billability >= target * 0.75) return 'bg-amber-100 dark:bg-amber-900/30'
  return 'bg-red-100 dark:bg-red-900/30'
}

const getBillabilityTextClass = billability => {
  if (billability === null) return 'text-gray-400'
  const target = props.employee.max_billability || 80
  if (billability >= target) return 'text-green-600 dark:text-green-400'
  if (billability >= target * 0.75) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

// Calculate average billability vs FTE (contractual hours)
const contractHoursPerWeek = computed(() => Number(props.employee.contractual_hours_per_week) || 40)

// Find current week index
const currentWeekIndex = computed(() => {
  const today = new Date().toISOString().split('T')[0]
  return weeklyData.value.findIndex(w => today >= w.week_start && today <= w.week_end)
})

// Helper: get projectmanagement hours for a date range from detailed hours
const getProjectmanagementHours = (startDate, endDate) => {
  return detailedHours.value
    .filter(h => {
      const date = String(h.start_date).split('T')[0]
      const typeLabel = (h.type_label || '').toLowerCase()
      return date >= startDate && date <= endDate && typeLabel === 'projectmanagement'
    })
    .reduce((sum, h) => sum + Number(h.hours || 0), 0)
}

// Past 8 weeks billability (excluding current week) vs FTE - only projectmanagement
const pastBillability = computed(() => {
  if (!weeklyData.value.length || currentWeekIndex.value < 0) return null

  // Get up to 8 weeks before current week
  const pastWeeks = weeklyData.value.slice(
    Math.max(0, currentWeekIndex.value - 8),
    currentWeekIndex.value
  )

  if (pastWeeks.length === 0) return null

  // Sum projectmanagement hours across all past weeks
  const totalPM = pastWeeks.reduce((sum, w) => {
    return sum + getProjectmanagementHours(w.week_start, w.week_end)
  }, 0)
  const totalContract = pastWeeks.length * contractHoursPerWeek.value

  if (totalContract === 0) return null
  return Math.round((totalPM / totalContract) * 100)
})

// Upcoming 8 weeks billability (excluding current week) vs FTE - only projectmanagement
const upcomingBillability = computed(() => {
  if (!weeklyData.value.length || currentWeekIndex.value < 0) return null

  // Get up to 8 weeks after current week
  const futureWeeks = weeklyData.value.slice(currentWeekIndex.value + 1, currentWeekIndex.value + 9)

  if (futureWeeks.length === 0) return null

  // Sum projectmanagement hours across all future weeks
  const totalPM = futureWeeks.reduce((sum, w) => {
    return sum + getProjectmanagementHours(w.week_start, w.week_end)
  }, 0)
  const totalContract = futureWeeks.length * contractHoursPerWeek.value

  if (totalContract === 0) return null
  return Math.round((totalPM / totalContract) * 100)
})

// Employee notes functionality
const employeeNotes = ref('')
const saveNotesTimer = ref(null)

// Load notes when employee changes
watch(
  () => props.employee.simplicate_id,
  async newId => {
    if (newId) {
      try {
        employeeNotes.value = await employeeService.getNotes(newId)
      } catch (error) {
        console.error('Failed to load employee notes:', error)
        employeeNotes.value = ''
      }
    }
  },
  { immediate: true }
)

// Debounced save function
const updateEmployeeNotes = () => {
  // Clear existing timer
  if (saveNotesTimer.value) {
    clearTimeout(saveNotesTimer.value)
  }

  // Set new timer to save after 1 second of no typing
  saveNotesTimer.value = setTimeout(async () => {
    try {
      await employeeService.updateNotes(props.employee.simplicate_id, employeeNotes.value)
    } catch (error) {
      console.error('Failed to save employee notes:', error)
    }
  }, 1000)
}
</script>
